import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";

const FIELDS = [
  ["Pillar / Vertical", [
    ["pillar_section", "Section label", "text"],
    ["pillar_strip_m", "Strip width (m)", "number"],
    ["pillar_thickness_mm", "Thickness (mm)", "number"],
    ["pillar_extra_kg", "Fixed add-on (kg/piece)", "number"],
  ]],
  ["Main Beam", [
    ["main_beam_section", "Section label", "text"],
    ["main_beam_strip_m", "Strip width (m)", "number"],
    ["main_beam_thickness_mm", "Thickness (mm)", "number"],
    ["main_beam_extra_kg", "Fixed add-on (kg/piece)", "number"],
  ]],
  ["Secondary Beam", [
    ["secondary_beam_section", "Section label", "text"],
    ["secondary_beam_strip_m", "Strip width (m)", "number"],
    ["secondary_beam_thickness_mm", "Thickness (mm)", "number"],
    ["secondary_beam_extra_kg", "Fixed add-on (kg/piece)", "number"],
    ["secondary_beam_gap_mm", "Target gap (mm)", "number"],
    ["secondary_beam_gap_min_mm", "Min gap (mm)", "number"],
    ["secondary_beam_gap_max_mm", "Max gap (mm)", "number"],
  ]],
  ["Flooring", [
    ["flooring_section", "Section label", "text"],
    ["flooring_strip_m", "Steel strip width (m) — for weight", "number"],
    ["flooring_finished_width_m", "Finished panel width (m) — for qty", "number"],
    ["flooring_thickness_mm", "Thickness (mm)", "number"],
    ["flooring_panel_length_m", "Panel length (m)", "number"],
  ]],
  ["Staircase", [
    ["staircase_weight_kg", "Reference weight (kg)", "number"],
    ["staircase_ref_height_mm", "Reference height (mm)", "number"],
    ["staircase_step_mm", "Step (mm)", "number"],
    ["staircase_riser_mm", "Riser (mm)", "number"],
  ]],
  ["Railing & Pricing", [
    ["railing_kg_per_metre", "Railing kg/m", "number"],
    ["density_factor", "Density factor", "number"],
    ["price_per_kg", "Price per kg (₹)", "number"],
    ["gst_percent", "GST %", "number"],
  ]],
];

export default function MezzanineSettings() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/mezzanine/config").then((r) => setCfg(r.data));
  }, []);

  const save = async () => {
    try {
      const payload = { ...cfg };
      for (const [, fields] of FIELDS) {
        for (const [k, , type] of fields) {
          if (type === "number") payload[k] = Number(payload[k]);
        }
      }
      const { data } = await api.put("/mezzanine/config", payload);
      setCfg(data);
      toast.success("Mezzanine settings updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (!cfg) return <Layout><div className="p-10 label-tech">Loading…</div></Layout>;

  return (
    <Layout>
      <PageHeader
        eyebrow="Admin · Mezzanine"
        title="Mezzanine master settings"
        subtitle="Section dimensions, per-piece add-ons, staircase and railing formulas, price per kg."
        actions={<button onClick={save} className="btn-primary" data-testid="mz-save-settings"><Save size={16} /> Save changes</button>}
      />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {FIELDS.map(([group, fields]) => (
          <div key={group} className="panel p-6 space-y-4" data-testid={`mz-group-${group.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="label-tech">{group}</div>
            {fields.map(([k, label, type]) => (
              <div key={k}>
                <label className="label-tech mb-1 block">{label}</label>
                <input
                  type={type}
                  step="any"
                  className="input-tech mono"
                  value={cfg[k] ?? ""}
                  onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })}
                  data-testid={`mz-field-${k}`}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </Layout>
  );
}
